import { useState } from "react";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  IconButton, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography,
} from "@mui/material";
import { PageHeader } from "@/components/PageHeader";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import CalculateIcon from "@mui/icons-material/Calculate";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  priceListsApi, type PriceListType, type ItemPickerRow,
} from "@/api/endpoints";
import { CatalogItemPicker } from "@/components/Pickers";
import { errorMessage } from "@/api/client";
import { useFeatureFlags } from "@/store/featureFlags";
import { TariffSelect } from "@/components/TariffSelect";
import {
  DerivationFields, absoluteDerivation, derivationError, derivationPayload,
} from "@/components/DerivationFields";
import { ItemPriceExplainDialog } from "@/features/pricing/ItemPriceExplainDialog";
import { todayIso } from "@/shared/datetime";

const TYPES: PriceListType[] = ["OP", "IP", "Corporate", "Insurance", "CustomerSpecific"];

interface DraftLine {
  key: string;
  item: ItemPickerRow | null;
  rate: number;
  effectiveFrom: string;
  effectiveTo: string;
}
const blankLine = (): DraftLine => ({
  key: crypto.randomUUID(), item: null, rate: 0,
  effectiveFrom: todayIso(), effectiveTo: "",
});

/**
 * UC-M2-PRC-01 — Price list CRUD. Lines panel uses the universal item picker (S2.1)
 * via a paste-a-UUID input in this thin first cut; a proper picker drops in S10 polish.
 */
export function PriceListsPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [listType, setListType] = useState<PriceListType>("OP");
  const [priority, setPriority] = useState(100);
  const [customerId, setCustomerId] = useState("");
  /** Rate card every line on this list joins (W11). "" = standalone lines. */
  const [tariffId, setTariffId] = useState("");
  /**
   * How every line on this list is priced (W13). Set at list level for the same reason the rate
   * card is: "corporate is MRP less fifteen" is one sentence about a whole agreement, and a
   * per-line basis picker in the table below would be unreadable. A mixed list is still expressible
   * through the API.
   */
  const [derivation, setDerivation] = useState(absoluteDerivation());
  const [lines, setLines] = useState<DraftLine[]>([blankLine()]);
  const [error, setError] = useState<string | null>(null);
  /** W17 — "why does this item cost this", the item-side twin of the service Resolve panel. */
  const [explainOpen, setExplainOpen] = useState(false);

  const query = useQuery({ queryKey: ["price-lists"], queryFn: () => priceListsApi.list({ pageSize: 200 }) });

  // Whether anything on this screen actually reaches a bill (W10).
  const featureFlags = useFeatureFlags();
  const priceListsLive = featureFlags.isEnabled("pricing.price_list");

  const save = useMutation({
    meta: { successMessage: "Price list saved" },
    mutationFn: () => priceListsApi.create({
      code, name, listType, priority,
      customerId: listType === "CustomerSpecific" ? customerId : undefined,
      lines: lines.map(l => ({
        itemId: l.item!.id, rate: l.rate,
        effectiveFrom: l.effectiveFrom,
        effectiveTo: l.effectiveTo || undefined,
        tariffId: tariffId || null,
        ...derivationPayload(derivation),
      })),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["price-lists"] });
      setOpen(false); setCode(""); setName(""); setListType("OP"); setPriority(100);
      setCustomerId(""); setTariffId(""); setDerivation(absoluteDerivation());
      setLines([blankLine()]);
    },
    onError: (e) => setError(errorMessage(e)),
  });
  const del = useMutation({
    mutationFn: (id: string) => priceListsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["price-lists"] }),
    onError: (e) => setError(errorMessage(e)),
  });

  const isDerived = derivation.derivation !== "Absolute";
  const derivationErr = derivationError(derivation, true);
  const validationError =
    !code ? "Code is required." :
    !name ? "Name is required." :
    (listType === "CustomerSpecific" && !customerId) ? "Pick a customer for a customer-specific list." :
    derivationErr ? derivationErr :
    // A derived list stores no amounts, so the rate column stops being a requirement.
    !lines.every(l => l.item && (isDerived || l.rate >= 0) && l.effectiveFrom)
      ? "Every line needs an item, a rate (≥ 0) and an effective-from date." :
    null;

  return (
    <Box>
      <PageHeader breadcrumbs={["Pricing"]} title="Price lists"
        actions={
          <>
            <Button variant="outlined" startIcon={<CalculateIcon />}
              onClick={() => setExplainOpen(true)}>
              Check a price
            </Button>
            <Button variant="contained" startIcon={<AddIcon />}
              onClick={() => { setError(null); setOpen(true); }}>
              New price list
            </Button>
          </>
        }
      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* W10 — a price list only prices anything when the tenant has the feature on. Without
          this banner the screen looks like it works while every bill quietly ignores it, which
          is exactly the confusion that kept this engine unwired for so long. */}
      {!featureFlags.isLoading && !priceListsLive && (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="pricelist-feature-off">
          Price lists are <b>not in use</b> for this tenant. You can build them here, but billing
          will keep charging batch MRP until <code>pricing.price_list</code> is enabled under
          Administration → Feature flags.
        </Alert>
      )}
      {priceListsLive && (
        <Alert severity="info" sx={{ mb: 2 }}>
          These lists price real bills. A line above the item&apos;s MRP is refused when you save
          it — billing above printed MRP is not permitted.
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Code</TableCell><TableCell>Name</TableCell>
            <TableCell>Type</TableCell><TableCell align="right">Priority</TableCell>
            <TableCell align="right">Lines</TableCell><TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {query.data?.items.map(p => (
              <TableRow key={p.id} hover>
                <TableCell><b>{p.code}</b></TableCell>
                <TableCell>{p.name}</TableCell>
                <TableCell><Chip size="small" variant="outlined" label={p.listType} /></TableCell>
                <TableCell align="right">{p.priority}</TableCell>
                <TableCell align="right">{p.lineCount}</TableCell>
                <TableCell>
                  <Chip size="small" color={p.isActive ? "success" : "default"}
                    label={p.isActive ? "Active" : "Inactive"} />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" color="error"
                    onClick={() => { if (confirm(`Delete price list ${p.code}?`)) del.mutate(p.id); }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <ClosableDialogTitle onClose={() => setOpen(false)}>New price list</ClosableDialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <TextField label="Code *" value={code} onChange={(e) => setCode(e.target.value)} sx={{ flex: 1 }} />
              <TextField label="Name *" value={name} onChange={(e) => setName(e.target.value)} sx={{ flex: 2 }} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField select label="Type *" value={listType}
                onChange={(e) => setListType(e.target.value as PriceListType)} sx={{ minWidth: 180 }}>
                {TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
              <TextField type="number" label="Priority"
                value={priority} onChange={(e) => setPriority(Number(e.target.value) || 0)}
                sx={{ width: 120 }} />
              {listType === "CustomerSpecific" && (
                <TextField label="Customer id *" placeholder="UUID"
                  value={customerId} onChange={(e) => setCustomerId(e.target.value)} sx={{ flex: 1 }} />
              )}
            </Stack>

            {/* W11 — set at list level and stamped onto every line. The column lives on the line
                so the API can express a mixed list, but a payer card in practice covers a whole
                list, and a per-line picker in the table below would be unreadable. */}
            <TariffSelect
              value={tariffId} onChange={setTariffId}
              helperText={tariffId
                ? "Every line below joins this card, and prices only while it is active and in date"
                : "Blank = standalone lines, priced as soon as the list is active"}
            />

            {/* W13 — "corporate is MRP less fifteen" is how item-side agreements are actually
                written. Stating it once here beats typing a discounted amount per item and
                re-typing all of them the next time an MRP moves. */}
            <DerivationFields
              value={derivation} onChange={setDerivation}
              allowMrp ownTariffId={tariffId || null} previewBase={100}
            />

            <Typography variant="subtitle2">Lines</Typography>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell sx={{ minWidth: 280 }}>Item *</TableCell>
                <TableCell align="right" sx={{ width: 120 }}>
                  {isDerived ? "Rate" : "Rate (₹) *"}
                </TableCell>
                <TableCell sx={{ width: 160 }}>Effective from *</TableCell>
                <TableCell sx={{ width: 160 }}>Effective to</TableCell>
                <TableCell sx={{ width: 60 }} />
              </TableRow></TableHead>
              <TableBody>
                {lines.map((l) => (
                  <TableRow key={l.key}>
                    <TableCell>
                      <CatalogItemPicker
                        value={l.item}
                        onChange={(v) =>
                          setLines(ls => ls.map(x => x.key === l.key ? { ...x, item: v } : x))}
                        placeholder="Search item…"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {/* A derived line carries no amount of its own — the server zeroes it. */}
                      <TextField type="number" size="small" sx={{ width: 110 }}
                        disabled={isDerived}
                        placeholder={isDerived ? "derived" : undefined}
                        value={isDerived ? "" : l.rate} onChange={(e) =>
                          setLines(ls => ls.map(x => x.key === l.key ? { ...x, rate: Number(e.target.value) || 0 } : x))}
                        inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }} />
                    </TableCell>
                    <TableCell>
                      <TextField type="date" size="small" InputLabelProps={{ shrink: true }}
                        value={l.effectiveFrom} onChange={(e) =>
                          setLines(ls => ls.map(x => x.key === l.key ? { ...x, effectiveFrom: e.target.value } : x))} />
                    </TableCell>
                    <TableCell>
                      <TextField type="date" size="small" InputLabelProps={{ shrink: true }}
                        value={l.effectiveTo} onChange={(e) =>
                          setLines(ls => ls.map(x => x.key === l.key ? { ...x, effectiveTo: e.target.value } : x))} />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small"
                        onClick={() => setLines(ls => ls.length === 1 ? ls : ls.filter(x => x.key !== l.key))}
                        disabled={lines.length === 1}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Button startIcon={<AddIcon />} size="small" sx={{ alignSelf: "flex-start" }}
              onClick={() => setLines(ls => [...ls, blankLine()])}>Add line</Button>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
          {validationError && <Alert severity="info" sx={{ mt: 2 }}>{validationError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!!validationError || save.isPending}
            onClick={() => save.mutate()}>Create</Button>
        </DialogActions>
      </Dialog>

      <ItemPriceExplainDialog open={explainOpen} onClose={() => setExplainOpen(false)} />
    </Box>
  );
}
